import { api } from './api';

// Define User type locally
export type Brand<T, K> = T & { readonly __brand: K };

export type UserId = Brand<string, 'UserId'>;
export type OrganizationId = Brand<string, 'OrganizationId'>;
export type RoleId = Brand<string, 'RoleId'>;

export interface User {
  id: UserId;
  email: string;
  secondaryEmail?: string;
  firstName: string;
  lastName: string;
  role: RoleId;
  secondaryRoles: string[];
  permissions: string[];
  organizationId?: OrganizationId;
  authProvider: 'local' | 'oidc';
  mfaEnabled: boolean;
  forcePasswordChange: boolean;
  isActive: boolean;
  preferences: {
    notifications: {
      email: boolean;
      sms: boolean;
      discord: boolean;
    };
    timezone: string;
    dateFormat: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface UserInfo {
  id: string;
  email: string;
  secondary_email: string | null;
  first_name: string;
  last_name: string;
  role: string;
  secondary_roles: string[];
  permissions: string[];
  organization_id: string | null;
  mfa_enabled: boolean;
  force_password_change: boolean;
}

// Registration types
export interface RegisterRequest {
  email: string;
  password: string;
  confirm_password: string;
  first_name: string;
  last_name: string;
  role: 'manager' | 'inspector' | 'client' | 'viewer';
}

export interface RegisterResponse {
  message: string;
  user_id: string;
  email: string;
  requires_verification: boolean;
  requires_approval: boolean;
}

export interface VerifyEmailResponse {
  message: string;
  email_verified: boolean;
  requires_approval: boolean;
}

export interface PendingUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  email_verified: boolean;
  approval_status: string;
  registered_at: string | null;
  created_at: string;
}

export const authService = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', {
      email,
      password,
    });
    return response.data;
  },

  async refresh(refreshToken: string): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  async logout(): Promise<void> {
    await api.post('/auth/logout');
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<UserInfo>('/auth/me');

    // Convert to User type
    return {
      id: response.data.id as any, // Cast to branded type
      email: response.data.email,
      secondaryEmail: response.data.secondary_email || undefined,
      firstName: response.data.first_name,
      lastName: response.data.last_name,
      role: response.data.role as any,
      secondaryRoles: response.data.secondary_roles || [],
      permissions: response.data.permissions as any[],
      organizationId: (response.data.organization_id || undefined) as any, // Cast to branded type
      authProvider: 'oidc',
      mfaEnabled: response.data.mfa_enabled,
      forcePasswordChange: response.data.force_password_change ?? false,
      isActive: true,
      preferences: {
        notifications: { email: true, sms: false, discord: false },
        timezone: 'America/Chicago',
        dateFormat: 'MMM d, yyyy',
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  },

  async oidcCallback(
    code: string,
    state: string,
    redirectUri: string
  ): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/oidc/callback', {
      code,
      state,
      redirect_uri: redirectUri,
    });
    return response.data;
  },

  // Registration methods
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await api.post<RegisterResponse>('/auth/register', data);
    return response.data;
  },

  async verifyEmail(token: string): Promise<VerifyEmailResponse> {
    const response = await api.post<VerifyEmailResponse>('/auth/verify-email', {
      token,
    });
    return response.data;
  },

  async resendVerification(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/auth/resend-verification',
      {
        email,
      }
    );
    return response.data;
  },

  // Password reset methods
  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/auth/forgot-password',
      { email }
    );
    return response.data;
  },

  async resetPassword(
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/auth/reset-password',
      {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }
    );
    return response.data;
  },

  // Admin user management
  async getPendingUsers(): Promise<PendingUser[]> {
    const response = await api.get<PendingUser[]>('/users/pending');
    return response.data;
  },

  async approveUser(
    userId: string,
    organizationId?: string
  ): Promise<{ message: string; user_id: string; organization_id?: string }> {
    const response = await api.post<{
      message: string;
      user_id: string;
      organization_id?: string;
    }>(`/users/${userId}/approve`, { organization_id: organizationId });
    return response.data;
  },

  async rejectUser(
    userId: string,
    reason: string
  ): Promise<{ message: string; user_id: string }> {
    const response = await api.post<{ message: string; user_id: string }>(
      `/users/${userId}/reject`,
      { reason }
    );
    return response.data;
  },
};
